// NotebookFrame.tsx — a scoped knowledge-base app, rendered ON the board.
//
// WHY (Erez, 2026-09-01): the Outform product requirements grew past 127,000
// characters in one page_content row. A single DocumentFrame of it is a scroll
// with no shape. He asked for "an app, like the knowledge base app" — chapters
// in a rail on the left, the chapter you picked on the right — sitting in the
// board section beside the two checklists that grade it.
//
// It is DocumentFrame's bigger sibling and shares its contract deliberately:
//   • same /api/kb/<slug> fetch, same BlockRenderer + architectureConfig
//   • same React-Flow traps — `nowheel` so the wheel scrolls the sheet instead
//     of zooming the canvas, `nodrag nopan` so drag-to-pan does not start on
//     the text, and an explicit pointerEvents:auto because React Flow sets
//     pointer-events:none on non-draggable nodes and silently kills scrolling.
//   • NOT an iframe, and it does not touch the shared Layout. Embedding the
//     real /knowledge-base route would drag the whole app chrome onto a board
//     node — the exact move that hung the dashboard in July 2026.
//
// The chapter list is DATA: every page under `prefix` appears automatically,
// ordered by nav_order, labelled by nav_label. Adding a chapter is one
// create_kb_page call and no deploy — same property that makes the KB index
// data-driven (MJOS-143).

import { useEffect, useState } from "react";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { useApi } from "../../../lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Chapter = { slug: string; label: string; order: number };
type Group = { label: string; prefix: string };
type Section = { label: string; chapters: Chapter[] };

export function NotebookFrame({
  prefix,
  groups,
  label,
  width = 1360,
  height = 1500,
  railWidth = 300,
}: {
  /** Slug prefix, e.g. "kb-doc/consultancy/outform/product/". Every page under it becomes a chapter. */
  prefix?: string;
  /**
   * TITLED, COLLAPSIBLE SECTIONS instead of one flat chapter list.
   *
   * Erez, 1 Sept: "I want to see a list of product pages and a list of
   * architecture pages with a title separating between them. And when I click
   * it, it collapses the list."
   *
   * The rail was a FLAT list sorted by nav_order and nothing else, so no amount
   * of re-ordering rows in the database could ever produce a separator — the
   * component had no concept of a group. That is why three passes at nav_order
   * changed nothing he could see: the fix was always here, not in the data.
   *
   * `prefix` still works exactly as before for every existing caller.
   */
  groups?: Group[];
  /** Caption above the frame. */
  label?: string;
  width?: number;
  height?: number;
  railWidth?: number;
}) {
  const api = useApi();
  const [chapters, setChapters] = useState<Chapter[] | null>(null);
  const [sections, setSections] = useState<Section[] | null>(null);
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [active, setActive] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 1 — the chapter list, from the same nav endpoint the KB index uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/kb-nav");
        if (!res.ok) {
          if (!cancelled) setError(`${res.status} — could not load the chapter list`);
          return;
        }
        const body = (await res.json()) as {
          pages: { page_slug: string; nav_label: string | null; nav_order: number | null }[];
        };
        if (cancelled) return;
        const toChapters = (pfx: string) =>
          (body.pages ?? [])
            .filter((p) => p.page_slug.startsWith(pfx))
            .map((p) => ({
              slug: p.page_slug,
              label: p.nav_label ?? p.page_slug.split("/").pop()!,
              order: p.nav_order ?? 999,
            }))
            .sort((a, b) => a.order - b.order || a.slug.localeCompare(b.slug));

        if (groups && groups.length) {
          const secs = groups
            .map((g) => ({ label: g.label, chapters: toChapters(g.prefix) }))
            .filter((sec) => sec.chapters.length > 0);
          setSections(secs);
          const flat = secs.flatMap((sec) => sec.chapters);
          setChapters(flat);
          setActive((cur) => cur ?? flat[0]?.slug ?? null);
          return;
        }

        const list = toChapters(prefix ?? "");
        setSections(null);
        setChapters(list);
        setActive((cur) => cur ?? list[0]?.slug ?? null);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `api` is intentionally not a dependency — useApi() returns a fresh
    // closure every render and including it re-runs this forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, JSON.stringify(groups ?? null)]);

  // 2 — the chapter itself.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setBlocks(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${active}`);
        if (!res.ok) {
          if (!cancelled) setError(`${res.status} — could not load ${active}`);
          return;
        }
        const body = (await res.json()) as { content?: { blocks?: Block[] } };
        if (!cancelled && Array.isArray(body.content?.blocks)) setBlocks(body.content!.blocks!);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const caption = label ?? "Notebook";

  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {caption}
          {chapters ? ` · ${chapters.length} chapters` : ""}
        </div>
        {active && (
          <a
            href={`/${active}`}
            className="nodrag nopan"
            style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none", whiteSpace: "nowrap" }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            open chapter ↗
          </a>
        )}
      </div>

      <div
        className="nowheel nodrag nopan"
        data-zoom
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          width,
          height,
          display: "flex",
          background: "#FFFFFF",
          border: "1px solid #E3DED4",
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* the rail */}
        <div
          style={{
            width: railWidth,
            flexShrink: 0,
            borderRight: "1px solid #EDE9E1",
            background: "#FAF9F6",
            overflowY: "auto",
            padding: "22px 14px",
            boxSizing: "border-box",
          }}
        >
          {!sections && (
            <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "#A8A79E", padding: "0 10px 12px" }}>
              Chapters
            </div>
          )}
          {!chapters && <div style={{ fontFamily: MONO, fontSize: 12, color: "#8C948E", padding: "0 10px" }}>loading…</div>}

          {/* TITLED, COLLAPSIBLE SECTIONS — click a title to collapse its list. */}
          {sections?.map((sec, i) => {
            const shut = !!closed[sec.label];
            return (
              <div key={sec.label} style={{ marginBottom: 14, marginTop: i === 0 ? 0 : 4 }}>
                <button
                  onClick={() => setClosed((c) => ({ ...c, [sec.label]: !c[sec.label] }))}
                  style={{
                    display: "flex", alignItems: "center", gap: 7, width: "100%", textAlign: "left",
                    border: "none", background: "transparent", cursor: "pointer",
                    padding: "0 10px 9px", font: "inherit",
                    fontFamily: MONO, fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.16em", textTransform: "uppercase", color: "#8C948E",
                    borderBottom: "1px solid #EDE9E1", marginBottom: 8,
                  }}
                >
                  <span style={{ display: "inline-block", width: 8, transform: shut ? "rotate(-90deg)" : "none", transition: "transform 120ms" }}>▾</span>
                  {sec.label}
                  <span style={{ marginLeft: "auto", fontWeight: 500, letterSpacing: 0, color: "#B4B3AA" }}>{sec.chapters.length}</span>
                </button>
                {!shut && sec.chapters.map((c) => {
                  const on = c.slug === active;
                  return (
                    <button key={c.slug} onClick={() => setActive(c.slug)}
                      style={{
                        display: "block", width: "100%", textAlign: "left", border: "none", cursor: "pointer",
                        background: on ? "#FFFFFF" : "transparent",
                        boxShadow: on ? "0 1px 3px rgba(27,33,30,0.08)" : "none",
                        borderRadius: 9, padding: "9px 11px", marginBottom: 2, font: "inherit",
                        fontSize: 13, lineHeight: 1.35, fontWeight: on ? 650 : 450,
                        color: on ? "#1B211E" : "#5C635E",
                      }}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {!sections && chapters?.map((c) => {
            const on = c.slug === active;
            return (
              <button
                key={c.slug}
                onClick={() => setActive(c.slug)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  cursor: "pointer",
                  background: on ? "#FFFFFF" : "transparent",
                  boxShadow: on ? "0 1px 3px rgba(27,33,30,0.08)" : "none",
                  borderRadius: 9,
                  padding: "9px 11px",
                  marginBottom: 2,
                  font: "inherit",
                  fontSize: 13,
                  lineHeight: 1.35,
                  fontWeight: on ? 650 : 450,
                  color: on ? "#1B211E" : "#5C635E",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* the chapter */}
        <div style={{ flex: 1, overflowY: "auto", padding: "44px 52px 56px", boxSizing: "border-box" }}>
          {error && <div style={{ fontFamily: MONO, fontSize: 13, color: "#C0492F" }}>{error}</div>}
          {!error && !blocks && <div style={{ fontFamily: MONO, fontSize: 13, color: "#8C948E" }}>loading…</div>}
          {blocks && <BlockRenderer config={architectureConfig} blocks={blocks} />}
        </div>
      </div>
    </div>
  );
}
